/* eslint-disable no-plusplus, class-methods-use-this */
import { Logger } from 'utilities';

/**
 * NetworkMonitor class for checking pending network requests
 */
export class NetworkMonitor {
  
  private readonly RECENT_REQUEST_THRESHOLD_MS = 5000;

  /**
   * Check if there are any pending network requests using Performance API
   */
  public hasPendingNetworkRequests(): boolean {
    try {
      // Use Performance API to check for pending requests
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

      // Check if any resource has responseEnd === 0 (indicating pending request)
      const hasPendingResources = resources.some(resource => resource.responseEnd === 0);

      if (hasPendingResources) {
        Logger.info('Found pending network requests via Performance API');
        return true;
      }

      // Additional check: look for recent requests that might still be processing
      const recentRequests = resources.filter(resource => {
        const now = performance.now();
        const requestTime = resource.startTime;
        const duration = now - requestTime;

        // If request started less than 5 seconds ago and responseEnd is 0, it might still be pending
        return duration < this.RECENT_REQUEST_THRESHOLD_MS && resource.responseEnd === 0;
      });

      if (recentRequests.length > 0) {
        Logger.info(`Found ${recentRequests.length} recent requests that might still be pending`);
        return true;
      }

      return false;
    } catch (error) {
      Logger.warn('Error checking pending requests via Performance API:', error);
      return false;
    }
  }

  /**
   * Monitor fetch and XMLHttpRequest calls to track pending requests
   */
  public setupNetworkMonitoring(): { hasPendingRequests: () => boolean; cleanup: () => void } {
    let pendingFetchRequests = 0;
    let pendingXHRRequests = 0;

    // Monitor fetch requests
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      pendingFetchRequests++;
      Logger.info(`Fetch request started (${pendingFetchRequests} pending)`);

      return originalFetch.apply(this, args).finally(() => {
        pendingFetchRequests--;
        Logger.info(`Fetch request completed (${pendingFetchRequests} pending)`);
      });
    };

    // Monitor XMLHttpRequest
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    // eslint-disable-next-line func-names
    XMLHttpRequest.prototype.open = function (
      method: string,
      url: string | URL,
      async: boolean = true,
      username?: string | null,
      password?: string | null
    ) {
      originalXHROpen.call(this, method, url, async, username, password);
      this.addEventListener(
        'loadend',
        () => {
          pendingXHRRequests--;
          Logger.info(`XHR request completed (${pendingXHRRequests} pending)`);
        },
        { once: true }
      );
    };

    // eslint-disable-next-line func-names
    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      pendingXHRRequests++;
      Logger.info(`XHR request started (${pendingXHRRequests} pending)`);
      originalXHRSend.call(this, body);
    };

    const hasPendingRequests = () => pendingFetchRequests > 0 || pendingXHRRequests > 0;

    const cleanup = () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.open = originalXHROpen;
      XMLHttpRequest.prototype.send = originalXHRSend;
    };

    return { hasPendingRequests, cleanup };
  }
}

